import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { siteSettingsService } from '../../services/siteSettingsService';
import { HowItWorksSkeleton } from '../../components/shared/ClientPageSkeleton';
import {
  ArrowRight,
  Sparkles,
  Star,
  Search,
  Calendar,
  CreditCard,
  CheckCircle,
  Shield,
  MessageSquare,
  Users,
} from 'lucide-react';

const ICON_MAP = {
  Search,
  Calendar,
  CreditCard,
  CheckCircle,
  Star,
  Shield,
  MessageSquare,
  Users,
};

const DEFAULT_HOW_IT_WORKS = {
  hero: {
    badge: 'Simple & Easy Process',
    title: 'How It Works',
    subtitle: "Planning your perfect event is simple with our streamlined process. From browsing vendors to enjoying your event, we've made it easy every step of the way.",
    primaryButtonText: 'Start Browsing',
    primaryButtonLink: '/browse',
    secondaryButtonText: 'Become a Vendor',
    secondaryButtonLink: '/vendor-signup',
  },
  steps: [
    { number: '01', icon: 'Search', title: 'Browse & Search', description: 'Explore our extensive directory of verified vendors. Filter by category, location, price range, and ratings to find the perfect match for your event.', color: 'from-blue-500 to-blue-600' },
    { number: '02', icon: 'Calendar', title: 'Compare & Select', description: 'View detailed profiles, portfolios, reviews, and pricing. Compare multiple vendors side-by-side to make an informed decision.', color: 'from-purple-500 to-purple-600' },
    { number: '03', icon: 'CreditCard', title: 'Book & Pay', description: 'Secure your booking with our safe payment system. Choose your preferred date, customize your package, and complete payment in minutes.', color: 'from-green-500 to-green-600' },
    { number: '04', icon: 'CheckCircle', title: 'Enjoy Your Event', description: 'Receive instant confirmation and stay connected with your vendor. On event day, sit back and enjoy while professionals handle everything.', color: 'from-orange-500 to-orange-600' },
  ],
  features: {
    title: 'Why Choose Our Platform',
    subtitle: "We've built a platform that makes event planning simple, secure, and enjoyable.",
    items: [
      { icon: 'Shield', title: 'Verified Vendors', description: 'All vendors undergo thorough verification and background checks to ensure quality and reliability.' },
      { icon: 'Star', title: 'Real Reviews', description: 'Read authentic reviews from previous clients to make confident decisions.' },
      { icon: 'CreditCard', title: 'Secure Payments', description: 'Your payments are protected with industry-standard encryption and secure processing.' },
      { icon: 'MessageSquare', title: 'Direct Communication', description: 'Chat directly with vendors to discuss details, ask questions, and customize your booking.' },
      { icon: 'Calendar', title: 'Easy Scheduling', description: 'Check availability in real-time and book your preferred date instantly.' },
      { icon: 'Users', title: '24/7 Support', description: 'Our support team is available around the clock to assist you with any questions or concerns.' },
    ],
  },
  cta: {
    title: 'Ready to Plan Your Event?',
    subtitle: 'Join thousands of satisfied clients who have found their perfect vendors through our platform.',
    primaryButtonText: 'Browse Vendors',
    primaryButtonLink: '/browse',
    secondaryButtonText: 'Create Account',
    secondaryButtonLink: '/register',
  },
};

const getIcon = (name) => {
  if (!name) return Star;
  return ICON_MAP[name] || Star;
};

const HowItWorks = () => {
  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });

  const hiw = settingsResponse?.data?.how_it_works;
  const hero = { ...DEFAULT_HOW_IT_WORKS.hero, ...hiw?.hero };
  const steps = Array.isArray(hiw?.steps) && hiw.steps.length > 0 ? hiw.steps : DEFAULT_HOW_IT_WORKS.steps;
  const featuresSection = { ...DEFAULT_HOW_IT_WORKS.features, ...hiw?.features, items: hiw?.features?.items?.length ? hiw.features.items : DEFAULT_HOW_IT_WORKS.features.items };
  const cta = { ...DEFAULT_HOW_IT_WORKS.cta, ...hiw?.cta };

  const ensureLink = (link) => (link?.startsWith('/') ? link : `/${link || ''}`);

  if (isLoading) {
    return <HowItWorksSkeleton />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 to-white border-b border-gray-100">
        <div className="page-container py-8 sm:py-12 md:py-20 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            {hero.badge && (
              <div className="inline-flex items-center gap-2 bg-black text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-light mb-4 sm:mb-6">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{hero.badge}</span>
              </div>
            )}
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-2">
              {hero.title}
            </h1>
            
            {hero.subtitle && (
              <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed max-w-3xl mx-auto px-2">
                {hero.subtitle}
              </p>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2">
              {hero.primaryButtonText && (
                <Link
                  to={ensureLink(hero.primaryButtonLink)}
                  className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-black text-white rounded-lg sm:rounded-xl font-light hover:bg-gray-800 transition-colors text-sm sm:text-base"
                >
                  {hero.primaryButtonText}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
              {hero.secondaryButtonText && (
                <Link
                  to={ensureLink(hero.secondaryButtonLink)}
                  className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 border-2 border-gray-300 text-gray-700 rounded-lg sm:rounded-xl font-light hover:border-gray-400 transition-colors text-sm sm:text-base"
                >
                  {hero.secondaryButtonText}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Steps Section */}
      <div className="py-8 sm:py-12 md:py-20 bg-white">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-12">
              {steps.map((step, index) => {
                const Icon = getIcon(step.icon);
                const stepColor = step.color || 'from-blue-500 to-blue-600';
                return (
                  <div 
                    key={index}
                    className="relative group"
                  >
                    <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-gray-200 hover:shadow-xl transition-all duration-300 h-full">
                      <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                        <div className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-lg sm:rounded-xl bg-gradient-to-br ${stepColor} flex items-center justify-center text-white text-base sm:text-lg md:text-xl font-bold`}>
                          {step.number}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                            <div className={`p-1.5 sm:p-2 rounded-lg bg-gradient-to-br ${stepColor} w-fit`}>
                              <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </div>
                            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                              {step.title}
                            </h3>
                          </div>
                          <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-8 sm:py-12 md:py-20 bg-gray-50">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
                {featuresSection.title}
              </h2>
              {featuresSection.subtitle && (
                <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-2">
                  {featuresSection.subtitle}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              {(featuresSection.items || []).map((feature, index) => {
                const Icon = getIcon(feature.icon);
                return (
                  <div 
                    key={index}
                    className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-gray-900 rounded-lg sm:rounded-xl mb-3 sm:mb-4">
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

     

      {/* CTA Section */}
      <div className="py-8 sm:py-12 md:py-20 bg-gray-900 text-white">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 px-2">
              {cta.title}
            </h2>
            {cta.subtitle && (
              <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto px-2">
                {cta.subtitle}
              </p>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2">
              {cta.primaryButtonText && (
                <Link
                  to={ensureLink(cta.primaryButtonLink)}
                  className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-white text-black rounded-lg sm:rounded-xl font-light hover:bg-gray-100 transition-colors text-sm sm:text-base"
                >
                  {cta.primaryButtonText}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
              {cta.secondaryButtonText && (
                <Link
                  to={ensureLink(cta.secondaryButtonLink)}
                  className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 border-2 border-white text-white rounded-lg sm:rounded-xl font-light hover:bg-white/10 transition-colors text-sm sm:text-base"
                >
                  {cta.secondaryButtonText}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;

